<script setup lang="ts">
/**
 * **設定樣板**（`/agent-config-templates`；批112／1.0.44）。
 *
 * 「一台代管主機要提供給站台的環境變數」的欄位宣告。1.0.43 之前那是產品的屬性
 * （`products.agent_config_schema_json`），於是某個產品的欄位鍵住進了 LS 的種子與
 * migration——**LS 是授權中台與代管中台，不為特定產品服務**，所以改成資料：
 * 在這一頁建立／匯入／編輯，再到主機頁掛上去。
 *
 * ★ 與〈產品 → 憑證設計〉刻意不是同一頁：那邊是產品**宣告**要我方在授權裡交付什麼
 *   （值進 payload），這邊是**主機層的執行環境**（值下發到站台 `.env`、不進 payload）。
 *
 * ★ `namespace` 是環境變數**前綴**：欄位代號 `FOO_TOKEN` ＋ 前綴 `ACME` ＝ `ACME_FOO_TOKEN`。
 *   編輯時即時預覽，因為那個字串最後會原樣變成客戶站台 `.env` 的一行。
 *
 * ★ 路由 `meta.roles: MANAGER` ＋ 側欄 nav 的 `roles`——後端寫端點是 `require_manager`。
 *   讀端點放寬到 staff（主機頁要拿它畫掛載下拉），但這一頁整頁都是寫操作。
 */
import { computed, onMounted, ref } from 'vue'

import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { AgentConfigTemplate, AgentConfigTemplateField, OrphanedValue } from '@/api/ls'
import { downloadJson } from '@/api/productIo'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const store = useDataStore()
const dialogs = useDialogsStore()

const rows = ref<AgentConfigTemplate[]>([])
const loading = ref(false)

/** 與後端 `services/agent_config.py` 的兩支正則**逐字相同**（兩邊都擋，訊息才一致）。 */
const NAMESPACE_RE = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/
const FIELD_KEY_RE = /^[A-Z][A-Z0-9_]{0,47}$/

async function load() {
  loading.value = true
  try {
    rows.value = await ls.listAgentConfigTemplates()
    store.agentConfigTemplates = rows.value
    store.agentConfigTemplatesLoaded = true
  } finally {
    loading.value = false
  }
}

onMounted(load)

async function fail(e: unknown, fallback: string) {
  const detail =
    e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  const msg =
    detail == null
      ? fallback
      : typeof detail === 'string'
        ? detail
        : ((detail as { message?: string }).message ?? JSON.stringify(detail))
  await dialogs.notify({ title: '操作失敗', message: msg })
}

// --- 編輯視窗 -------------------------------------------------------------
const editorOpen = ref(false)
const editing = ref<AgentConfigTemplate | null>(null)
const saving = ref(false)
const draft = ref<{
  name: string
  namespace: string
  description: string
  fields: AgentConfigTemplateField[]
}>({ name: '', namespace: '', description: '', fields: [] })

function blankField(): AgentConfigTemplateField {
  return {
    key: '',
    label: '',
    type: 'string',
    secret: false,
    absent_default: null,
    hint: null,
    deprecated: false,
  }
}

function openCreate() {
  editing.value = null
  draft.value = { name: '', namespace: '', description: '', fields: [blankField()] }
  editorOpen.value = true
}

function openEdit(row: AgentConfigTemplate) {
  editing.value = row
  draft.value = {
    name: row.name,
    namespace: row.namespace,
    description: row.description ?? '',
    fields: row.fields.map((f) => ({ ...f })),
  }
  editorOpen.value = true
}

/** 即時預覽的 env 名——與後端 `agent_config.env_name()` 同一條規則。 */
function envPreview(key: string): string {
  const ns = draft.value.namespace.trim()
  const k = key.trim()
  if (!ns || !k) return '—'
  return `${ns}_${k}`
}

const namespaceOk = computed(() => NAMESPACE_RE.test(draft.value.namespace.trim()))
const fieldsOk = computed(() => {
  const keys = draft.value.fields.map((f) => f.key.trim())
  if (keys.some((k) => !FIELD_KEY_RE.test(k))) return false
  return new Set(keys).size === keys.length
})
const valid = computed(
  () => !!draft.value.name.trim() && namespaceOk.value && fieldsOk.value,
)

function addField() {
  draft.value.fields = [...draft.value.fields, blankField()]
}
function removeField(i: number) {
  draft.value.fields = draft.value.fields.filter((_, idx) => idx !== i)
}

/** 型別改了就把只屬於舊型別的欄位清掉（不然送出去會 422，而畫面上看不出原因）。 */
function onTypeChange(f: AgentConfigTemplateField) {
  if (f.type !== 'number') {
    f.min = undefined
    f.max = undefined
  }
  if (f.type !== 'string') f.options = undefined
  f.absent_default = null
}

function cleanFields(): AgentConfigTemplateField[] {
  return draft.value.fields.map((f) => {
    const out: AgentConfigTemplateField = {
      key: f.key.trim(),
      label: f.label.trim() || f.key.trim(),
      type: f.type,
      secret: !!f.secret,
      absent_default:
        f.absent_default === '' || f.absent_default == null
          ? null
          : f.type === 'number'
            ? Number(f.absent_default)
            : f.type === 'bool'
              ? String(f.absent_default) === 'true'
              : String(f.absent_default),
      hint: (f.hint ?? '').toString().trim() || null,
      deprecated: !!f.deprecated,
    }
    if (f.type === 'number') {
      if (f.min != null && String(f.min) !== '') out.min = Number(f.min)
      if (f.max != null && String(f.max) !== '') out.max = Number(f.max)
    }
    if (f.type === 'string' && f.options?.length) out.options = f.options
    return out
  })
}

async function submit() {
  if (!valid.value || saving.value) return
  saving.value = true
  const body: ls.AgentConfigTemplateIn = {
    name: draft.value.name.trim(),
    namespace: draft.value.namespace.trim(),
    description: draft.value.description.trim() || null,
    fields: cleanFields(),
  }
  try {
    if (editing.value) {
      const out = await ls.updateAgentConfigTemplate(editing.value.id, body)
      await warnOrphans(out.orphaned_values)
    } else {
      await ls.createAgentConfigTemplate(body)
    }
    editorOpen.value = false
    await load()
  } catch (e) {
    await fail(e, editing.value ? '更新失敗' : '建立失敗')
  } finally {
    saving.value = false
  }
}

async function warnOrphans(orphans: OrphanedValue[]) {
  if (!orphans.length) return
  await dialogs.notify({
    title: '有主機的已存值失去對應',
    message:
      '這些值還在資料庫裡，但已經沒有欄位對得上（不會下發、後台也看不到）：'
      + orphans.map((o) => `${o.host} / ${o.namespace}_${o.key}`).join('、')
      + '。請到那幾台主機重新設定。',
  })
}

async function remove(row: AgentConfigTemplate) {
  const ok = await dialogs.confirm({
    title: '刪除設定樣板',
    message: `刪除「${row.name}」（前綴 ${row.namespace}）？有主機掛著就刪不掉，請先卸載。`,
    danger: true,
    confirmText: '刪除',
  })
  if (!ok) return
  try {
    await ls.deleteAgentConfigTemplate(row.id)
    await load()
  } catch (e) {
    await fail(e, '刪除失敗')
  }
}

/** 匯出＝**下載一個 JSON 檔**（匯入格式）。

 * 批112 原本是把 JSON 塞進「匯入設定樣板」視窗的文字框給人複製——按了「匯出」卻跳出
 * 標題寫「匯入」的視窗，主人 09-17 回報「點下載卻出現匯入設定樣板」「沒有一個下載 json 的
 * 功能」。檔名帶 namespace，與〈產品〉頁的匯出走同一支 `downloadJson`。 */
async function exportOne(row: AgentConfigTemplate) {
  try {
    const body = await ls.exportAgentConfigTemplate(row.id)
    const filename = `agent-config-template-${row.namespace}.json`
    downloadJson(filename, body)
    await dialogs.notify({
      title: '已下載樣板 JSON',
      message: `已存成 ${filename}（匯入格式，可直接在另一台 LS 的〈匯入 JSON〉貼上或選檔）。`,
    })
  } catch (e) {
    await fail(e, '匯出失敗')
  }
}

// --- 匯入視窗 -------------------------------------------------------------
const importOpen = ref(false)
const importText = ref('')
const importBusy = ref(false)
const importPreview = ref<{ create: string[]; overwrite: string[] } | null>(null)
const importError = ref('')
/** 選檔（批171：原生檔案欄位換成按鈕＋檔名，與站內其他上傳一致）。 */
const importFileInput = ref<HTMLInputElement | null>(null)
const importFileName = ref('')

function openImport() {
  importText.value = ''
  importFileName.value = ''
  importPreview.value = null
  importError.value = ''
  importOpen.value = true
}

function parseImport(): ls.AgentConfigTemplateIn[] | null {
  try {
    const parsed = JSON.parse(importText.value)
    const list = Array.isArray(parsed) ? parsed : [parsed]
    if (!list.length) throw new Error('空的')
    for (const item of list) {
      if (!item || typeof item !== 'object' || !item.name || !item.namespace) {
        throw new Error('每一份都要有 name 與 namespace')
      }
    }
    return list as ls.AgentConfigTemplateIn[]
  } catch (e) {
    importError.value = `讀不懂這份 JSON：${e instanceof Error ? e.message : String(e)}`
    return null
  }
}

/** 先預覽「將新建 N／覆蓋 M」再確認——匯入是 upsert，覆蓋是不可逆的。 */
function previewImport() {
  importError.value = ''
  importPreview.value = null
  const list = parseImport()
  if (!list) return
  const existing = new Set(rows.value.map((r) => r.name))
  importPreview.value = {
    create: list.filter((x) => !existing.has(x.name)).map((x) => x.name),
    overwrite: list.filter((x) => existing.has(x.name)).map((x) => x.name),
  }
}

async function submitImport() {
  const list = parseImport()
  if (!list) return
  importBusy.value = true
  try {
    const out = await ls.importAgentConfigTemplates(list)
    importOpen.value = false
    await load()
    await warnOrphans(out.orphaned_values)
    await dialogs.notify({
      title: '匯入完成',
      message: `新建 ${out.created.length} 份、覆蓋 ${out.updated.length} 份。`,
    })
  } catch (e) {
    await fail(e, '匯入失敗')
  } finally {
    importBusy.value = false
  }
}

async function pickFile(ev: Event) {
  const input = ev.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  importFileName.value = file.name
  importText.value = await file.text()
  importPreview.value = null
  importError.value = ''
  input.value = ''
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>設定樣板</h1>
      <span class="count">{{ rows.length }}</span>
      <span class="spacer" />
      <UiButton variant="secondary" @click="openImport">匯入 JSON</UiButton>
      <UiButton variant="primary" @click="openCreate">＋ 新增樣板</UiButton>
    </div>

    <div class="card">
      <p class="subtle">
        設定樣板是「一台代管主機要提供給站台的<strong>環境變數</strong>」的欄位宣告。
        <code class="mono">namespace</code> 是環境變數前綴，欄位代號是後綴——組出來的
        <code class="mono">NAMESPACE_KEY</code> 會原樣寫進站台的 <code class="mono">.env</code>。
        中台<strong>不解讀任何鍵的意義</strong>；產品要交付欄位宣告，就出貨一份匯入格式的
        JSON 給我方匯入。建好之後到〈代管主機 › 主機詳情〉把它掛到主機上。
      </p>
      <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>名稱</th><th>前綴（namespace）</th><th>欄位數</th><th>掛載主機</th><th>說明</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.id">
              <td>{{ r.name }}</td>
              <td><code class="mono">{{ r.namespace }}</code></td>
              <td class="subtle">
                {{ r.fields.length }}
                <span v-if="r.fields.some((f) => f.secret)" title="含加密欄位">🔒</span>
              </td>
              <td class="subtle">{{ r.attached_hosts }}</td>
              <td class="subtle desc">{{ r.description ?? '—' }}</td>
              <td class="acts-cell">
                <div class="acts">
                  <button class="x" title="匯出 JSON（下載檔案）" @click="exportOne(r)">⤓</button>
                  <button class="x" title="編輯" @click="openEdit(r)">✎</button>
                  <button
                    class="x danger"
                    :disabled="r.attached_hosts > 0"
                    :title="r.attached_hosts > 0 ? '有主機掛著，請先卸載' : '刪除'"
                    @click="remove(r)"
                  >✕</button>
                </div>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="6" class="subtle empty-row">
                還沒有任何設定樣板。產品出貨的樣板 JSON 可以用右上〈匯入 JSON〉帶進來。
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 建立／編輯 -->
    <UiModal
      :open="editorOpen"
      :title="editing ? `編輯設定樣板 — ${editing.name}` : '新增設定樣板'"
      width="900px"
      @close="editorOpen = false"
    >
      <div class="form-grid two">
        <label class="field">
          <span class="field-label">名稱（匯入以它為鍵）</span>
          <input v-model="draft.name" maxlength="80" placeholder="例：某產品的代管站共用設定" />
        </label>
        <label class="field">
          <span class="field-label">前綴 namespace</span>
          <input v-model="draft.namespace" class="mono" maxlength="32" placeholder="例：ACME" />
          <p v-if="draft.namespace.trim() && !namespaceOk" class="hint bad">
            要大寫字母開頭、只含大寫英數與底線分段（不可結尾底線）。
          </p>
        </label>
      </div>
      <label class="field notes">
        <span class="field-label">說明</span>
        <input v-model="draft.description" maxlength="200" placeholder="選填" />
      </label>

      <div class="fields-head">
        <strong>欄位</strong>
        <span class="count">{{ draft.fields.length }}</span>
        <span class="spacer" />
        <UiButton size="sm" variant="secondary" @click="addField">＋ 加一個欄位</UiButton>
      </div>
      <!-- 批113：原本是 11 欄的一列表格（代號／env 名／中文名／型別／🔒／預設值／min／max／
           說明／停用／刪除），modal 900px 內只有 889px 可用而表格要 1006px，右邊三四欄永遠
           在橫捲外——主人 09-17 回報「欄位很多要編排一下」。改成**一個欄位一張卡、兩行**：
           第一行是「這個欄位是什麼」（代號＋即時 env 預覽、型別、加密、停用、刪除），
           第二行是「值怎麼定」（中文名、預設值、數值範圍、說明）；用 `.field` 共用樣式讓每格
           自帶標籤，不再靠表頭對位。-->
      <div class="fld-list">
        <div v-for="(f, i) in draft.fields" :key="i" class="fld-card">
          <div class="fld-row head">
            <label class="field">
              <!-- env 預覽放在標籤同一行（不另佔一行）：這一格若比旁邊高，型別／加密／停用／刪除
                   就會對齊到預覽那一行、整排比輸入框低一格（主人 09-17 回報「對齊有問題」）。 -->
              <span class="field-label">
                代號（key）
                <code
                  v-if="envPreview(f.key) !== '—'"
                  class="mono envp"
                  title="寫進站台 .env 的環境變數名（namespace_代號）"
                >→ {{ envPreview(f.key) }}</code>
                <span v-else class="envp subtle">（填了代號會顯示環境變數名）</span>
              </span>
              <input v-model="f.key" class="mono" placeholder="FOO_TOKEN" />
            </label>
            <label class="field">
              <span class="field-label">型別</span>
              <select v-model="f.type" @change="onTypeChange(f)">
                <option value="string">字串</option>
                <option value="number">數值</option>
                <option value="bool">布林</option>
              </select>
            </label>
            <label class="chk" title="值需加密：後台只顯示遮罩、下發為密文、不可由派工參數覆寫">
              <input v-model="f.secret" type="checkbox" />
              🔒 加密
            </label>
            <label class="chk" title="停用：後台灰掉、不下發">
              <input v-model="f.deprecated" type="checkbox" />
              停用
            </label>
            <button class="x danger fld-del" title="刪除這個欄位" @click="removeField(i)">✕</button>
          </div>
          <div class="fld-row">
            <label class="field">
              <span class="field-label">中文名</span>
              <input v-model="f.label" />
            </label>
            <label class="field">
              <span class="field-label">預設值</span>
              <select v-if="f.type === 'bool'" v-model="f.absent_default">
                <option :value="null">（無）</option>
                <option :value="true">開啟</option>
                <option :value="false">關閉</option>
              </select>
              <input
                v-else-if="!f.secret"
                v-model="f.absent_default"
                :type="f.type === 'number' ? 'number' : 'text'"
              />
              <input v-else value="—（加密欄位不設預設值）" disabled />
            </label>
            <template v-if="f.type === 'number'">
              <label class="field">
                <span class="field-label">min</span>
                <input v-model="f.min" type="number" />
              </label>
              <label class="field">
                <span class="field-label">max</span>
                <input v-model="f.max" type="number" />
              </label>
            </template>
          </div>
          <!-- 說明獨立一行：放進上面那個 auto-fit grid 會讓字串型的兩格被撐成四欄寬度（空 track 收不掉）。 -->
          <label class="field">
            <span class="field-label">說明</span>
            <input v-model="f.hint" maxlength="200" placeholder="選填，給後台填值的人看" />
          </label>
        </div>
        <p v-if="!draft.fields.length" class="subtle empty-row">還沒有欄位——按〈＋ 加一個欄位〉。</p>
      </div>
      <p v-if="draft.fields.length && !fieldsOk" class="hint bad">
        欄位代號要大寫字母開頭、只含大寫英數與底線（最長 48），而且同一份樣板內不可重複。
      </p>
      <p class="subtle">
        🔒 勾了「加密」的欄位：後台只顯示頭尾遮罩、下發給 agent 時是密文、
        <strong>不可</strong>由派工參數覆寫。改 namespace 或刪掉欄位時，已經掛著這份樣板的
        主機上的對應值會失去對應（存檔後會列出來），請到那幾台重新設定。
      </p>
      <template #foot>
        <UiButton @click="editorOpen = false">取消</UiButton>
        <UiButton variant="primary" :loading="saving" :disabled="!valid" @click="submit">
          {{ editing ? '儲存' : '建立' }}
        </UiButton>
      </template>
    </UiModal>

    <!-- 匯入 -->
    <UiModal :open="importOpen" title="匯入設定樣板" width="720px" @close="importOpen = false">
      <p class="subtle">
        貼上產品出貨的樣板 JSON（單一物件或陣列都收）。<strong>依名稱 upsert</strong>：
        同名的整份覆蓋、沒有的就建立。先按〈預覽〉看清楚會動到哪幾份再確認。
      </p>
      <div class="field">
        <span class="field-label">選檔（選填）</span>
        <div class="file-pick">
          <input ref="importFileInput" type="file" accept="application/json,.json" hidden @change="pickFile" />
          <UiButton variant="secondary" @click="importFileInput?.click()">選擇 JSON 檔…</UiButton>
          <span class="subtle file-name">{{ importFileName || '未選擇檔案（也可以直接貼在下面）' }}</span>
        </div>
      </div>
      <textarea v-model="importText" class="json" rows="14" placeholder='{ "name": "...", "namespace": "ACME", "fields": [...] }' />
      <p v-if="importError" class="hint bad">{{ importError }}</p>
      <div v-if="importPreview" class="preview">
        <p>將<strong>新建 {{ importPreview.create.length }}</strong> 份<span v-if="importPreview.create.length">：{{ importPreview.create.join('、') }}</span></p>
        <p>將<strong>覆蓋 {{ importPreview.overwrite.length }}</strong> 份<span v-if="importPreview.overwrite.length">：{{ importPreview.overwrite.join('、') }}</span></p>
      </div>
      <template #foot>
        <UiButton @click="importOpen = false">取消</UiButton>
        <UiButton variant="secondary" :disabled="!importText.trim()" @click="previewImport">預覽</UiButton>
        <UiButton
          variant="primary"
          :loading="importBusy"
          :disabled="!importPreview"
          @click="submitImport"
        >
          確認匯入
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
  flex-wrap: wrap;
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.desc {
  max-width: 320px;
}
.x {
  border: none;
  background: none;
  color: var(--fg-4);
  cursor: pointer;
  font-size: var(--fs-13);
  padding: 2px 4px;
  border-radius: var(--r-sm);
}
.x:hover {
  background: var(--bg-hover);
  color: var(--fg);
}
.x.danger:hover {
  color: var(--danger);
}
.x:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.mono {
  font-family: ui-monospace, monospace;
}
.form-grid.two {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.field.notes {
  margin-top: var(--sp-3);
}
.fields-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin: var(--sp-4) 0 var(--sp-2);
  font-size: var(--fs-13);
}
/* 批113：欄位改成「一張卡兩行」（見樣板註解）。第一行固定五格：代號吃最寬、型別次之、
   兩個勾選與刪除鈕只佔內容寬；第二行 auto-fit，數值型多出 min／max 兩小格也塞得進 860px。 */
.fld-list {
  display: grid;
  gap: var(--sp-2);
}
.fld-card {
  display: grid;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border: 1px solid var(--border-2);
  border-radius: var(--r-md);
  background: var(--bg-elevated, var(--bg));
}
.fld-row {
  display: grid;
  /* 字串／布林型是兩格（各半），數值型多 min／max 成四格（各約 200px）——都不折行。 */
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--sp-3);
  align-items: start;
}
.fld-row.head {
  grid-template-columns: minmax(220px, 1.6fr) minmax(110px, 0.8fr) auto auto auto;
  /* 每格都是「標籤一行＋36px 控制項」，頂端對齊就會整排等高；沒有標籤的勾選與刪除
     以 `align-self: end`＋同樣 36px 高貼齊輸入框那一行。 */
  align-items: start;
}
.fld-card input:not([type='checkbox']),
.fld-card select {
  width: 100%;
  min-width: 0;
  height: 36px;
  font-size: var(--fs-12);
}
.chk {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  align-self: end;
  font-size: var(--fs-12);
  color: var(--fg-2);
  white-space: nowrap;
  cursor: pointer;
}
.fld-del {
  height: 36px;
  align-self: end;
}
.envp {
  margin-left: 6px;
  font-size: var(--fs-11);
  font-weight: 400;
  color: var(--fg-3);
  overflow-wrap: anywhere;
}
.file-pick {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  min-width: 0;
}
.file-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.json {
  width: 100%;
  font-family: ui-monospace, monospace;
  font-size: var(--fs-12);
  margin-top: var(--sp-2);
}
.preview {
  margin-top: var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  background: var(--bg);
  border-radius: var(--r-sm);
  font-size: var(--fs-12);
}
.preview p {
  margin: 2px 0;
}
.hint {
  margin: 4px 0 0;
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.hint.bad {
  color: var(--danger);
}
/* 手機（批171）：欄位卡第一行五格擠不下（型別、預設值、加密勾選被切出右緣）→
   代號獨佔一列，型別＋加密＋停用＋刪除排第二列；第二行的 auto-fit 本來就會收成單欄。 */
@media (--mobile) {
  .fld-row.head {
    grid-template-columns: minmax(0, 1fr) auto auto auto;
  }
  .fld-row.head > .field:first-child {
    grid-column: 1 / -1;
  }
  .fld-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
